
import { supabase } from "@/integrations/supabase/client";
import { uploadMultipleImages } from "@/utils/uploadUtils";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";

interface PropertyData {
  seller_id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  property_type: string;
  foundation_type: string | null;
  year_built: number | null;
  lot_size: string | null;
  roof_age: string | null;
  window_type: string | null;
  furnace_age: string | null;
  water_heater_age: string | null;
  architecture_style: string | null;
  parking_type: string | null;
  price: number;
  arv_value: number | null;
  zillow_link: string | null;
  contract_expiration_date: string | null;
  status: string;
  description: string | null;
  image_url?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export const saveDraftProperty = async (
  sellerId: string,
  partialData: Partial<PropertyData>,
  selectedImages: File[],
  onUploadProgress?: (completed: number, total: number) => void
): Promise<{ success: boolean; propertyId?: string; error?: string }> => {

  try {
    const draftPayload = {
      seller_id: sellerId,
      status: 'draft',
      address: partialData.address || '',
      city: partialData.city || '',
      state: partialData.state || '',
      zip_code: partialData.zip_code || '',
      bedrooms: partialData.bedrooms ?? 0,
      bathrooms: partialData.bathrooms ?? 0,
      square_feet: partialData.square_feet ?? 0,
      property_type: partialData.property_type || 'single-family',
      price: partialData.price ?? 0,
      foundation_type: partialData.foundation_type || null,
      year_built: partialData.year_built || null,
      lot_size: partialData.lot_size || null,
      roof_age: partialData.roof_age || null,
      window_type: partialData.window_type || null,
      furnace_age: partialData.furnace_age || null,
      water_heater_age: partialData.water_heater_age || null,
      architecture_style: partialData.architecture_style || null,
      parking_type: partialData.parking_type || null,
      arv_value: partialData.arv_value || null,
      zillow_link: partialData.zillow_link || null,
      contract_expiration_date: partialData.contract_expiration_date || null,
      description: partialData.description || null,
      image_url: null,
      latitude: partialData.latitude || null,
      longitude: partialData.longitude || null,
    };

    const { data: propertyResult, error: propertyError } = await supabase
      .from('properties')
      .insert(draftPayload)
      .select('id')
      .single();

    if (propertyError) {
      logger.error("Draft creation error", { error: propertyError.message });
      return { success: false, error: `Failed to save draft: ${propertyError.message}` };
    }

    const propertyId = propertyResult.id;

    // Upload images if provided
    if (selectedImages.length > 0) {
      try {
        const folderPath = `property-${propertyId}`;
        const imageUrls = await uploadMultipleImages(selectedImages, 'properties', folderPath, 5, onUploadProgress);
        if (imageUrls.length > 0) {
          const imageRecords = imageUrls.map((url, index) => ({
            property_id: propertyId,
            image_url: url,
            is_primary: index === 0,
            display_order: index + 1,
          }));
          await supabase.from('property_images').insert(imageRecords);
          await supabase.from('properties').update({ image_url: imageUrls[0] }).eq('id', propertyId);
        }
      } catch (uploadError) {
        logger.error("[PropertyService] Draft image upload error", { error: uploadError });
      }
    }

    return { success: true, propertyId };
  } catch (error) {
    logger.error("Error saving draft", { error });
    return { success: false, error: "An unexpected error occurred" };
  }
};

export const createProperty = async (
  propertyData: PropertyData,
  selectedImages: File[],
  onUploadProgress?: (completed: number, total: number) => void
): Promise<{ success: boolean; propertyId?: string; error?: string }> => {

  logger.debug("[PropertyService] Starting property creation", { 
    address: propertyData.address,
    imageCount: selectedImages.length 
  });
  
  try {
    // Check for duplicate active properties first
    const { data: duplicateCheck, error: duplicateError } = await supabase
      .from('properties')
      .select('id, address, city, state, zip_code, seller_id, price')
      .eq('address', propertyData.address.trim())
      .eq('city', propertyData.city.trim())
      .eq('state', propertyData.state.trim())
      .eq('zip_code', propertyData.zip_code.trim())
      .eq('status', 'active')
      .limit(1);

    if (duplicateError) {
      logger.error("Error checking for duplicate property", { error: duplicateError.message });
      return { 
        success: false, 
        error: "Failed to verify property uniqueness. Please try again." 
      };
    }

    if (duplicateCheck && duplicateCheck.length > 0) {
      const existingProperty = duplicateCheck[0];
      return {
        success: false,
        error: `This property is already listed as active on GoDyspo at ${existingProperty.address}, ${existingProperty.city}, ${existingProperty.state} ${existingProperty.zip_code}. Each property can only have one active listing at a time.`
      };
    }
    // Step 1: Create property FIRST (without image_url) to get the propertyId
    // This mirrors the Edit flow where the property already exists before image uploads
    const { data: propertyResult, error: propertyError } = await supabase
      .from('properties')
      .insert({ ...propertyData, image_url: null })
      .select('id')
      .single();
    
    if (propertyError) {
      logger.error("Property creation error", { error: propertyError.message });
      return { 
        success: false, 
        error: `Failed to create property: ${propertyError.message}` 
      };
    }
    
    const propertyId = propertyResult.id;
    logger.debug("[PropertyService] Property created, now handling images", { propertyId });

    // Step 2: Upload images using the real property ID (matches Edit flow)
    let imageUrls: string[] = [];
    
    if (selectedImages.length > 0) {
      logger.debug("[PropertyService] Starting image uploads", { 
        count: selectedImages.length,
        sizes: selectedImages.map(f => `${f.name}: ${f.size} bytes`)
      });
      
      toast({
        title: "Uploading images...",
        description: `Processing ${selectedImages.length} images`,
      });
      
      // Use the actual property UUID for folder path (consistent with Edit flow)
      const folderPath = `property-${propertyId}`;
      
      try {
        // Upload the images to Supabase storage in batches
        imageUrls = await uploadMultipleImages(
          selectedImages, 
          'properties', 
          folderPath,
          5, // Batch size (will be reduced to 2 on native platforms automatically)
          onUploadProgress
        );

        
        logger.debug("[PropertyService] Image upload complete", { 
          successful: imageUrls.length, 
          total: selectedImages.length 
        });
      } catch (uploadError) {
        logger.error("[PropertyService] Image upload threw error", { error: uploadError });
        imageUrls = []; // Continue with no images rather than failing
      }
      
      // Step 3: Save ALL images to property_images table (like Edit flow)
      if (imageUrls.length > 0) {
        const imageRecords = imageUrls.map((url, index) => ({
          property_id: propertyId,
          image_url: url,
          is_primary: index === 0,
          display_order: index + 1
        }));
        
        const { error: imagesError } = await supabase
          .from('property_images')
          .insert(imageRecords);
        
        if (imagesError) {
          logger.error("Property images insert error", { error: imagesError.message });
        }
        
        // Step 4: Update properties.image_url with the first image
        const { error: updateError } = await supabase
          .from('properties')
          .update({ image_url: imageUrls[0] })
          .eq('id', propertyId);
        
        if (updateError) {
          logger.error("Property image_url update error", { error: updateError.message });
        }
        
        const message = imageUrls.length === selectedImages.length
          ? `All ${imageUrls.length} images uploaded successfully`
          : `${imageUrls.length} of ${selectedImages.length} images uploaded (${selectedImages.length - imageUrls.length} failed)`;
        
        toast({
          title: imageUrls.length === selectedImages.length ? "Upload complete" : "Partial upload",
          description: message,
          variant: imageUrls.length === selectedImages.length ? "default" : "destructive",
        });
      } else {
        // Step 5: Clear messaging when all uploads fail
        logger.warn("[PropertyService] All image uploads failed");
        toast({
          title: "Images failed to upload",
          description: "Property created without images. Add them from the Edit page.",
          variant: "destructive",
          duration: 8000,
        });
      }
    } else {
      logger.debug("[PropertyService] No images to upload");
    }
    
    // Step 6: Send notifications to interested buyers
    try {
      logger.debug("Triggering buyer notifications for new property");
      await supabase.functions.invoke('send-property-notification', {
        body: {
          property_id: propertyId,
          property_data: {
            address: propertyData.address,
            city: propertyData.city,
            state: propertyData.state,
            price: propertyData.price,
            bedrooms: propertyData.bedrooms,
            bathrooms: propertyData.bathrooms,
            square_feet: propertyData.square_feet,
            image_url: imageUrls.length > 0 ? imageUrls[0] : null
          }
        }
      });
    } catch (notificationError) {
      // Don't fail property creation if notifications fail
      logger.error("Failed to send buyer notifications", { error: notificationError });
    }
    
    return { 
      success: true, 
      propertyId 
    };
  } catch (error) {
    logger.error("Error saving property", { error });
    return { 
      success: false, 
      error: "An unexpected error occurred" 
    };
  }
};

export const updateProperty = async (
  propertyId: string,
  propertyData: Partial<PropertyData>
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Update the property in the database
    const { error } = await supabase
      .from('properties')
      .update(propertyData)
      .eq('id', propertyId);
    
    if (error) {
      logger.error("Property update error", { error: error.message });
      return { 
        success: false, 
        error: `Failed to update property: ${error.message}` 
      };
    }
    
    return { success: true };
  } catch (error) {
    logger.error("Error updating property", { error });
    return { 
      success: false, 
      error: "An unexpected error occurred" 
    };
  }
};

export const updatePropertyStatus = async (
  propertyId: string,
  status: string,
  closedData?: {
    closedDate?: string;
    salePrice?: number;
    financingType?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const updateData: any = { status };
    
    // If status is "sold" and we have closed data, include it
    if (status === "sold" && closedData) {
      if (closedData.closedDate) {
        updateData.closed_date = closedData.closedDate;
      }
      if (closedData.salePrice) {
        updateData.sale_price = closedData.salePrice;
      }
      if (closedData.financingType) {
        updateData.financing_type = closedData.financingType;
      }
    }
    
    // Update the property status
    const { error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', propertyId);
    
    if (error) {
      logger.error("Property status update error", { error: error.message });
      return { 
        success: false, 
        error: `Failed to update property status: ${error.message}` 
      };
    }
    
    return { success: true };
  } catch (error) {
    logger.error("Error updating property status", { error });
    return { 
      success: false, 
      error: "An unexpected error occurred" 
    };
  }
};
